import React, { useState, useEffect, use } from 'react';
import { Download, Reply } from '@mui/icons-material';
import { Snackbar, Alert } from '@mui/material';
import { formatFileSize, formatDate } from './videoUtils';
import { UserImage } from '@/api/imageApi';
import { useUpdateImageMetadata } from '@/hooks/querry_hooks/dashboard/useImageMutations';
import { useUpdateVideoMetadata } from '@/hooks/querry_hooks/dashboard/useVideoMutations';
import DeleteConfirmModal from '@/components/dashboard/modals/DeleteConfirmModal';
import { DeviceContext } from '@/hooks/common/DeviceContext';
import {
  noOutlineHandlers,
  createRedHoverHandlers,
  createGrayHoverHandlers,
  createDarkGrayHoverHandlers,
  getVideoTypeLabel,
  copyToClipboard,
  buttonStyles,
  createTitleSaveHandler,
} from '@/utils/common/modal/modalUtils';
import {
  handleDownloadVideoFrame,
  handleDownloadImage,
  handleDownloadVideo as utilsHandleDownloadVideo,
  handleVideoDelete,
  handleImageDelete,
  renderContentPreview as utilsRenderContentPreview,
  renderContentInfo as utilsRenderContentInfo,
  renderAIModelInfo as utilsRenderAIModelInfo,
} from '@/utils/admin/videoEditUtils';
import { renderLoadingSpinner, StatusRadioButton, renderModalHeader, renderMobileBadges } from '@/utils/common/modal/contentRenderUtils';

type ContentItem = UserVideo | UserImage | null | undefined;

interface UserVideo {
  id: string;
  video_url: string;
  thumbnail_url: string;
  title: string;
  file_size: number;
  created_at: string;
  updated_at: string;
  user_email?: string;
  user_id?: string;
  path?: string;
  status?: string;
  feedback?: string;
  prompt?: string;
  model?: string;
  video_type?: string;
  is_pinned?: boolean;
  pinned_by?: string;
  pinned_at?: string;
  delete_request?: boolean;
  aspect_ratio?: string;
  original_video_id?: string;
}

interface VideoEditModalProps {
  isAdmin: boolean;
  video?: UserVideo | null;
  image?: UserImage | null;
  isOpen: boolean;
  onClose: () => void;
  onDelete?: (itemId: string) => Promise<void>;
  onDeleteVideo?: (videoId: string) => Promise<void>;
  onDeleteImage?: (imageId: string) => Promise<void>;
  handleDownloadLastFrame?: (signedUrl: string) => Promise<void>;
  contentType?: 'video' | 'image';
  adminEmail?: string;
}

// Helper function to determine content type
const getContentType = (contentType: string | undefined, video: UserVideo | null | undefined, image: UserImage | null | undefined) => {
  const isVideo = contentType === 'video' || (!contentType && video);
  const isImage = contentType === 'image' || (!contentType && image);
  const currentItem = isVideo ? video : image;
  return { isVideo, isImage, currentItem };
};

// Helper function to check edit permissions
const checkEditPermission = (isAdmin: boolean, adminEmail: string | undefined, currentItem: ContentItem) => {
  return isAdmin || (adminEmail && currentItem?.user_email && currentItem.user_email === adminEmail);
};

// Helper to create update handler
const createUpdateHandler = (params: {
  currentItem: ContentItem;
  status: string;
  feedback: string;
  isImage: boolean;
  isVideo: boolean;
  image: UserImage | null | undefined;
  video: UserVideo | null | undefined;
  adminEmail: string | undefined;
  updateImageMutation: ReturnType<typeof useUpdateImageMetadata>;
  updateVideoMutation: ReturnType<typeof useUpdateVideoMetadata>;
  onClose: () => void;
}) => () => {
  if (!params.currentItem) return;
  
  const backendStatus = params.status.toLowerCase();
  params.onClose();
  
  if (params.isImage && params.image) {
    params.updateImageMutation.mutate({
      imageId: params.image.id,
      status: backendStatus,
      feedback: params.feedback,
      previousStatus: params.image.status,
      userEmail: params.image.user_email,
      adminEmail: params.adminEmail || 'unknown-admin',
    });
    return;
  }
  
  if (params.isVideo && params.video) {
    params.updateVideoMutation.mutate({
      videoId: params.video.id,
      status: backendStatus,
      feedback: params.feedback,
      previousStatus: params.video.status,
      userEmail: params.video.user_email,
      adminEmail: params.adminEmail || 'unknown-admin',
    });
  }
};

// Helper to create delete handler
const createDeleteHandler = (params: {
  currentItem: ContentItem;
  isVideo: boolean;
  isImage: boolean;
  onDeleteVideo: ((videoId: string) => Promise<void>) | undefined;
  onDelete: ((itemId: string) => Promise<void>) | undefined;
  onDeleteImage: ((imageId: string) => Promise<void>) | undefined;
  video: UserVideo | null | undefined;
  image: UserImage | null | undefined;
  contentTypeLabel: string;
  setIsDeleting: React.Dispatch<React.SetStateAction<boolean>>;
  onClose: () => void;
}) => async () => {
  if (!params.currentItem) return;
  
  params.setIsDeleting(true);
  try {
    if (params.isVideo) {
      await handleVideoDelete(params.onDeleteVideo || params.onDelete, params.video);
    } else if (params.isImage) {
      await handleImageDelete(params.onDeleteImage || params.onDelete, params.image);
    }
    params.onClose();
  } catch (error) {
    console.error(`Failed to delete ${params.contentTypeLabel.toLowerCase()}:`, error);
  } finally {
    params.setIsDeleting(false);
  }
};

// Helper components to reduce cognitive complexity
const DownloadButton: React.FC<{
  isVideo: boolean;
  onClick: () => void;
  isDownloading: boolean;
}> = ({ isVideo, onClick, isDownloading }) => (
  <button
    onClick={onClick}
    className="flex items-center justify-center transition-colors disabled:opacity-50"
    style={{ ...buttonStyles.iconButton, border: '1px solid #d1d5db' }}
    disabled={isDownloading}
    title={isVideo ? "Download Video" : "Download"}
    {...createGrayHoverHandlers(isDownloading)}
  >
    {isDownloading ? renderLoadingSpinner('gray') : <Download className="w-6 h-6 text-gray-600" />}
  </button>
);

const CopyLinkButton: React.FC<{
  onCopyLink: () => void;
  linkCopied: boolean;
}> = ({ onCopyLink, linkCopied }) => (
  <button
    onClick={onCopyLink}
    className="flex items-center justify-center transition-colors"
    style={{ ...buttonStyles.iconButton, border: '1px solid #d1d5db' }}
    title={linkCopied ? "Copied!" : "Copy link"}
    {...createGrayHoverHandlers(false)}
  >
    {linkCopied ? (
      <span className="text-gray-600 text-xs font-medium">Copied!</span>
    ) : (
      <Reply className="w-6 h-6 text-gray-600" style={{ transform: 'scaleX(-1)' }} />
    )}
  </button>
);

const DeleteButton: React.FC<{
  onClick: () => void;
  disabled: boolean;
}> = ({ onClick, disabled }) => (
  <button
    onClick={onClick}
    className="flex items-center justify-center transition-colors text-red-600 disabled:opacity-50"
    style={{ ...buttonStyles.iconButton, border: '1px solid #fca5a5' }}
    disabled={disabled}
    title="Delete"
    {...createRedHoverHandlers()}
  >
    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
    </svg>
  </button>
);

const LastFrameButton: React.FC<{
  onClick: () => void;
  isDownloading: boolean;
}> = ({ onClick, isDownloading }) => (
  <button
    onClick={onClick}
    className="text-white transition-colors disabled:opacity-50 flex items-center justify-center"
    style={buttonStyles.primaryButton}
    disabled={isDownloading}
    {...createDarkGrayHoverHandlers(isDownloading)}
  >
    {isDownloading ? (
      <>
        {renderLoadingSpinner('white')}
        <span>Downloading...</span>
      </>
    ) : (
      <>
        <Download className="w-5 h-5" />
        <span>Last Frame</span>
      </>
    )}
  </button>
);

const ActionButton: React.FC<{
  hasChanges: boolean;
  onUpdate: () => void;
  onClose: () => void;
  disabled: boolean;
  isMobile?: boolean;
}> = ({ hasChanges, onUpdate, onClose, disabled, isMobile }) => {
  if (hasChanges) {
    return (
      <button
        onClick={onUpdate}
        className={`text-white transition-colors disabled:opacity-50 flex items-center justify-center ${isMobile ? 'flex-1' : ''}`}
        style={buttonStyles.primaryButton}
        disabled={disabled}
        {...createDarkGrayHoverHandlers(disabled)}
      >
        Update
      </button>
    );
  }
  return (
    <button
      onClick={onClose}
      className={`text-gray-700 transition-colors ${isMobile ? 'flex-1' : ''}`}
      style={buttonStyles.secondaryButton}
      disabled={disabled}
      {...createGrayHoverHandlers(false)}
    >
      Close
    </button>
  );
};

// Footer buttons component to reduce complexity
const EditModalFooterButtons: React.FC<{
  showDeleteConfirm: boolean;
  isVideo: boolean;
  isAdmin: boolean;
  hasChanges: boolean;
  isDownloadingFrame: boolean;
  isDownloadingVideo: boolean;
  isDeleting: boolean;
  video: UserVideo | null | undefined;
  handleDownloadLastFrame?: (signedUrl: string) => Promise<void>;
  handleDownloadVideo: () => Promise<void>;
  handleDownloadFrame: () => Promise<void>;
  handleUpdate: () => void;
  onClose: () => void;
  setShowDeleteConfirm: (show: boolean) => void;
  onCopyLink: () => void;
  linkCopied: boolean;
}> = ({
  showDeleteConfirm,
  isVideo,
  isAdmin,
  hasChanges,
  isDownloadingFrame,
  isDownloadingVideo,
  isDeleting,
  video,
  handleDownloadLastFrame,
  handleDownloadVideo,
  handleDownloadFrame,
  handleUpdate,
  onClose,
  setShowDeleteConfirm,
  onCopyLink,
  linkCopied,
}) => (
  <>
    {/* Mobile Layout */}
    <div className="md:hidden flex flex-col gap-3">
      <div className="flex items-center gap-3">
        {!showDeleteConfirm && (
          <DownloadButton
            isVideo={isVideo}
            onClick={isVideo ? handleDownloadVideo : handleDownloadFrame}
            isDownloading={isDownloadingFrame || isDownloadingVideo}
          />
        )}
        {!showDeleteConfirm && isVideo && video && (
          <CopyLinkButton onCopyLink={onCopyLink} linkCopied={linkCopied} />
        )}
        {!showDeleteConfirm && isAdmin && (
          <ActionButton
            hasChanges={hasChanges}
            onUpdate={handleUpdate}
            onClose={onClose}
            disabled={isDeleting || isDownloadingFrame}
            isMobile
          />
        )}
      </div>
      <div className="flex items-center gap-3">
        {!showDeleteConfirm && isAdmin && (
          <DeleteButton
            onClick={() => setShowDeleteConfirm(true)}
            disabled={isDownloadingFrame}
          />
        )}
        {!showDeleteConfirm && isVideo && video && handleDownloadLastFrame && (
          <div className="flex-1">
            <LastFrameButton
              onClick={handleDownloadFrame}
              isDownloading={isDownloadingFrame || isDownloadingVideo}
            />
          </div>
        )}
      </div>
    </div>
    {/* Desktop Layout */}
    <div className="hidden md:flex items-center justify-between" style={{ gap: '12px' }}>
      <div className="flex items-center" style={{ gap: '12px' }}>
        {!showDeleteConfirm && isAdmin && (
          <DeleteButton
            onClick={() => setShowDeleteConfirm(true)}
            disabled={isDeleting || isDownloadingFrame || isDownloadingVideo}
          />
        )}
        {!showDeleteConfirm && (
          <DownloadButton
            isVideo={isVideo}
            onClick={isVideo ? handleDownloadVideo : handleDownloadFrame}
            isDownloading={isDownloadingFrame || isDownloadingVideo}
          />
        )}
        {!showDeleteConfirm && isVideo && video && (
          <CopyLinkButton onCopyLink={onCopyLink} linkCopied={linkCopied} />
        )}
      </div>
      <div className="flex items-center" style={{ gap: '12px' }}>
        {!showDeleteConfirm && isVideo && video && handleDownloadLastFrame && (
          <LastFrameButton
            onClick={handleDownloadFrame}
            isDownloading={isDownloadingFrame || isDownloadingVideo}
          />
        )}
        {!showDeleteConfirm && isAdmin && (
          <ActionButton
            hasChanges={hasChanges}
            onUpdate={handleUpdate}
            onClose={onClose}
            disabled={isDeleting || isDownloadingFrame || isDownloadingVideo}
          />
        )}
      </div>
    </div>
  </>
);

const VideoEditModal: React.FC<VideoEditModalProps> = (props) => {
  const {
    isAdmin,
    video,
    image,
    isOpen,
    onClose,
    onDelete,
    onDeleteVideo,
    onDeleteImage,
    handleDownloadLastFrame,
    contentType,
    adminEmail,
  } = props;

  const { isMobile } = use(DeviceContext) ?? { isTablet: false, isMobile: false };

  const [status, setStatus] = useState('Pending');
  const [feedback, setFeedback] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isDownloadingFrame, setIsDownloadingFrame] = useState(false);
  const [isDownloadingVideo, setIsDownloadingVideo] = useState(false);
  const [showCopiedMessage, setShowCopiedMessage] = useState(false);
  const [showFeedback, setShowFeedback] = useState(false);
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [editedTitle, setEditedTitle] = useState('');
  const [showEditErrorToast, setShowEditErrorToast] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);

  const handleCopyLink = () => {
    if (video?.id) {
      navigator.clipboard.writeText(globalThis.location.origin + '/dashboard?video=' + video.id);
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 2000);
    }
  };

  // Track initial values to detect changes
  const [initialStatus, setInitialStatus] = useState('Pending');
  const [initialFeedback, setInitialFeedback] = useState('');

  // React Query mutations for instant updates
  const updateImageMutation = useUpdateImageMetadata();
  const updateVideoMutation = useUpdateVideoMetadata();

  // Determine content type and current item
  const { isVideo, isImage, currentItem } = getContentType(contentType, video, image);
  const contentTypeLabel = isVideo ? 'Video' : 'Image';
  const canEditTitle = checkEditPermission(isAdmin, adminEmail, currentItem);

  // Reset modal state when item changes
  useEffect(() => {
    if (currentItem) {
      // Normalize status to capitalized format (Pending, Approved, Rejected)
      const itemStatus = currentItem.status 
        ? currentItem.status.charAt(0).toUpperCase() + currentItem.status.slice(1).toLowerCase()
        : 'Pending';
      const itemFeedback = currentItem.feedback || '';
      setStatus(itemStatus);
      setFeedback(itemFeedback);
      setInitialStatus(itemStatus);
      setInitialFeedback(itemFeedback);
      setShowDeleteConfirm(false);
      setEditedTitle(currentItem.title || '');
      setIsEditingTitle(false);
    }
  }, [currentItem]);

  // Check if there are any changes
  const hasChanges = () => {
    return status !== initialStatus || feedback !== initialFeedback;
  };

  const handleUpdate = createUpdateHandler({
    currentItem,
    status,
    feedback,
    isImage: Boolean(isImage),
    isVideo: Boolean(isVideo),
    image,
    video,
    adminEmail,
    updateImageMutation,
    updateVideoMutation,
    onClose
  });

  const handleDelete = createDeleteHandler({
    currentItem,
    isVideo: Boolean(isVideo),
    isImage: Boolean(isImage),
    onDeleteVideo,
    onDelete,
    onDeleteImage,
    video,
    image,
    contentTypeLabel,
    setIsDeleting,
    onClose
  });

  const handleDownloadFrame = async () => {
    setIsDownloadingFrame(true);
    try {
      if (isVideo) {
        await handleDownloadVideoFrame(video, handleDownloadLastFrame);
      } else if (isImage) {
        await handleDownloadImage(image);
      }
    } finally {
      setIsDownloadingFrame(false);
    }
  };

  const handleDownloadVideo = async () => {
    setIsDownloadingVideo(true);
    try {
      await utilsHandleDownloadVideo(video);
    } catch (error) {
      console.error('Error downloading video:', error);
    } finally {
      setIsDownloadingVideo(false);
    }
  };

  const handleCopyPrompt = () => {
    const prompt = video?.prompt || image?.prompt;
    if (prompt) {
      copyToClipboard(prompt, setShowCopiedMessage);
    }
  };

  const handleSaveTitle = createTitleSaveHandler({
    getCurrentTitle: () => currentItem?.title,
    getEditedTitle: () => editedTitle,
    setEditedTitle,
    setIsEditing: setIsEditingTitle,
    canEdit: Boolean(canEditTitle),
    onEditDenied: () => setShowEditErrorToast(true),
    onOptimisticUpdate: (trimmedTitle) => {
      // Immediately update the UI
      if (isVideo && video) {
        video.title = trimmedTitle;
      } else if (isImage && image) {
        image.title = trimmedTitle;
      }
    },
    onSave: async (trimmedTitle) => {
      if (isVideo && video) {
        await updateVideoMutation.mutateAsync({ videoId: video.id, title: trimmedTitle });
      } else if (isImage && image) {
        await updateImageMutation.mutateAsync({ imageId: image.id, title: trimmedTitle });
      }
    },
  });

  const videoTypeLabel = getVideoTypeLabel(video?.video_type);
  const imageTypeLabel = getVideoTypeLabel(image?.image_type);
  const contentTitle = currentItem?.title || `[${isVideo ? 'Video' : 'Image'}/File Name]`;

  if (!isOpen || !currentItem) {
    return null;
  }

  return (
    <>
      {/* Delete Confirmation Modal */}
      <DeleteConfirmModal
        showDeleteConfirm={showDeleteConfirm}
        isRequestingDelete={isDeleting}
        isAdmin={true}
        hasDeleteRequest={false}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={handleDelete}
        getDeleteConfirmationMessage={() => 'Are you sure you want to confirm deletion?'}
      />

      {/* Main Video/Image Edit Modal */}
      <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl w-[766px] max-h-[90vh] overflow-y-auto shadow-2xl">
        {/* Modal Header with Editable Title */}
        {renderModalHeader(
          contentTitle,
          isVideo ? videoTypeLabel : imageTypeLabel,
          currentItem.status,
          onClose,
          {
            isEditing: isEditingTitle,
            editedValue: editedTitle,
            canEdit: Boolean(canEditTitle),
            onStartEdit: () => {
              if (!canEditTitle) {
                setShowEditErrorToast(true);
                return;
              }
              setIsEditingTitle(true);
            },
            onChange: (val) => {
              if (!canEditTitle) {
                setShowEditErrorToast(true);
                setIsEditingTitle(false);
                return;
              }
              setEditedTitle(val);
            },
            onSave: () => {
              handleSaveTitle();
            },
            onCancel: () => {
              setEditedTitle(currentItem?.title || '');
              setIsEditingTitle(false);
            },
            onEditDenied: () => {
              setIsEditingTitle(false);
              setShowEditErrorToast(true);
            },
          }
        )}

        {/* Modal Content */}
        <div className="px-6 pb-6 space-y-4">
          {/* Content Preview */}
          <div
            className={`${isImage ? 'w-full max-w-md mx-auto flex items-center justify-center' : 'aspect-video bg-gray-100'} rounded-xl overflow-hidden shadow-md`}
            style={isImage ? { maxHeight: '500px' } : {}}
          >
            {utilsRenderContentPreview(isVideo === true, video, isImage === true, image)}
          </div>

          {/* Content Info Grid */}
          <div className="space-y-3">
          {/* Delete Request Warning Banner - Below Video */}
          {currentItem?.delete_request && (
            <div className="flex items-center px-4 py-3 rounded-lg" style={{ backgroundColor: '#fecaca', border: '1px solid #fca5a5' }}>
              <svg
                className="w-5 h-5 mr-3 flex-shrink-0"
                fill="none"
                stroke="#991b1b"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <span style={{ fontFamily: 'Maven Pro', fontWeight: 600, fontSize: '16px', color: '#991b1b' }}>
                Content creator has requested deletion.
              </span>
            </div>
          )}

            {utilsRenderContentInfo(currentItem, formatDate, formatFileSize, isMobile)}
            {/* Mobile Badges - Below Size for mobile only */}
              {renderMobileBadges(
                isVideo ? videoTypeLabel : imageTypeLabel,
                currentItem.status
              )}
            {/* AI Model and Prompt Info */}
            {utilsRenderAIModelInfo(isVideo === true, video, isImage === true, image, showCopiedMessage, handleCopyPrompt)}


          </div>

          {/* Status Selection - Admin only */}
          {isAdmin && (
            <div className="p-4 rounded-2xl" style={{ backgroundColor: '#F0F7FF' }}>
              {/* New Status heading and radio buttons */}
              <div className={`flex ${isMobile ? 'flex-col' : 'items-center'} gap-4 mb-4 ${isMobile ? '' : 'flex-nowrap'}`}>
                
                
                <div className={`flex ${isMobile ? 'flex-col' : 'items-center'} gap-${isMobile ? '3' : '4'}`}>
                  <StatusRadioButton value="Pending" currentStatus={status} label="Pending" onChange={setStatus} />
                  <StatusRadioButton value="Approved" currentStatus={status} label="Approved" onChange={setStatus} />
                  <StatusRadioButton value="Rejected" currentStatus={status} label="Rejected" onChange={setStatus} />
                </div>
              </div>

              {/* Feedback Dropdown - Bottom Right */}
              <div className="flex justify-end">
                <button
                  onClick={() => setShowFeedback(!showFeedback)}
                  className="flex items-center gap-2 text-left transition-colors"
                  style={{ 
                    outline: 'none', 
                    background: 'transparent',
                    border: 'none',
                    padding: 0
                  }}
                  {...noOutlineHandlers}
                >
                  <span style={{ fontFamily: 'Maven Pro', fontWeight: 'bold', fontStyle: 'normal', fontSize: '18px', lineHeight: '100%', letterSpacing: '0%', color: '#2563eb' }}>
                    Feedback (Optional)
                  </span>
                  <svg
                    className={`w-5 h-5 text-blue-600 transition-transform ${showFeedback ? 'rotate-180' : ''}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </button>
              </div>
              
              {showFeedback && (
                <div className="mt-3">
                  <textarea
                    id='feedback-textarea'
                    value={feedback}
                    onChange={(e) => setFeedback(e.target.value)}
                    rows={3}
                    className="w-full px-3 bg-white py-2 border border-gray-300 rounded-2xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    style={{ fontFamily: 'Maven Pro', fontSize: '18px', lineHeight: '26px' }}
                    placeholder="Enter your comments..."
                  />
                </div>
              )}
            </div>
          )}
        </div>

        {/* Modal Footer */}
        <div className="px-6 pb-6 pt-2">
          <EditModalFooterButtons
            showDeleteConfirm={showDeleteConfirm}
            isVideo={Boolean(isVideo)}
            isAdmin={isAdmin}
            hasChanges={hasChanges()}
            isDownloadingFrame={isDownloadingFrame}
            isDownloadingVideo={isDownloadingVideo}
            isDeleting={isDeleting}
            video={video}
            handleDownloadLastFrame={handleDownloadLastFrame}
            handleDownloadVideo={handleDownloadVideo}
            handleDownloadFrame={handleDownloadFrame}
            handleUpdate={handleUpdate}
            onClose={onClose}
            setShowDeleteConfirm={setShowDeleteConfirm}
            onCopyLink={handleCopyLink}
            linkCopied={linkCopied}
          />
        </div>
      </div>
      </div>

      {/* Toast Notification for Edit Error */}
      <Snackbar
        open={showEditErrorToast}
        autoHideDuration={3000}
        onClose={() => setShowEditErrorToast(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        sx={{ zIndex: 9999 }}
      >
        <Alert
          onClose={() => setShowEditErrorToast(false)}
          severity="error"
          sx={{ width: '100%' }}
        >
          Users cannot edit other users title
        </Alert>
      </Snackbar>
    </>
  );
};

export default VideoEditModal;
